import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { getFilteredTableData } from "../../../../../../shared/utils/tableUtils"
import { Box, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, Tooltip } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles } from "tss-react/mui"
import { MRT_ColumnDef, MRT_RowSelectionState, MRT_SortingState, MRT_TableInstance } from "material-react-table"
import {
    faArrowProgress,
    faCircleEuro,
    faFileArrowDown,
    faList,
    faLock,
    faLockOpen,
    faPrint,
    faReceipt,
    faTrash,
    faUser,
} from "@fortawesome/pro-light-svg-icons"
import { StandardTwoLinesCell } from "../../../../../../shared/components/table/StandardTwoLinesCell"
import { GROW_CELL_CLASS, useGridLayoutStyles } from "../../../../../../shared/components/table/useGridLayoutStyles"
import { COLUMN_SIZE } from "../../../../../../shared/constants/table/config"

import { useExportMeetingServiceOrdersToCsv } from "../components/useExportMeetingServiceOrdersToCsv"
import { useRefreshBeforeExport, withFreshRows } from "../../components/useRefreshBeforeExport"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { ConfirmModal } from "../../../../../../shared/components/dialogs/ConfirmModal"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import { useAppSelector } from "../../../../../store/storeHooks"
import { useMatchEnvironment } from "../../../../../environment/envHooks"
import { PRINT_CHAT_ENVIRONMENTS } from "../../../../../../shared/components/orderChat/print/usePrintWithChatHistory"
import { selectEffectiveUserId } from "../../../../../context/contextSelectors"
import {
    ActionBarItem,
    ActionBarMenu,
    ActionBarMenuItem,
    FloatingActionBar,
} from "../../../../../../shared/components/table/FloatingActionBar"
import {
    MeetingServiceBookDialogContent,
    MeetingServiceDeleteDialogContent,
} from "../components/MeetingServiceDialogContent"
import {
    changeBookStatus,
    changeLockStatusMerchant,
    deleteMeetingServiceOrders,
    fetchMeetingServiceOrders,
    MeetingServiceOrdersResponse,
    MerchantMeetingServiceOrdersQuery,
} from "../meetingServiceApi"
import { ORDERS_SEARCH_MIN_LENGTH } from "../../../../../../shared/components/filters/constants/searchConst"
import {
    DeliveryType,
    DeliveryTypeFilter,
    FilteredStatuses,
    getTimePeriod,
    MeetingServiceOrder,
    MeetingServiceOrderStatus,
    MeetingServiceOrdersView,
    TimePeriodMeetingService,
} from "../meetingServiceTypes"
import {
    filterMeetingServiceOrders,
    getHiddenOrderStatuses,
    getOrdersDeliveryRange,
    type MeetingServiceClientSideFilters,
} from "../meetingServiceLib"
import { useMeetingServiceFilters } from "../context/MeetingServiceFiltersContext"
import { useLocationQuery } from "../../../../../../core/router/routerHooks"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { useProductionPeriodFilter } from "../../hooks/useProductionPeriodFilter"
import {
    TableNumberOfItemsBox,
    TableSummaryWrap,
    TableTotalAmountBox,
} from "../../../../../../shared/components/table/TableSummaryBox"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { OrderPackageSlip } from "../components/OrderPackageSlip"
import { MinimizedStatusButton, StatusButton } from "../components/StatusMenu"
import { StatusChangeModal } from "../components/StatusChangeModal"
import { useQueryClient } from "@tanstack/react-query"
import { useMeetingServiceStatusUndo } from "../useMeetingServiceStatusUndo"
import { UserMeetingsReceipt } from "../../../../../nonAdminUser/reports/MeetingsReceipt/UserMeetingsReceipt"

import type { ProductionOrderMenuContentProps } from "../components/ProductionOrderMenu"
import { ProductionOrderMenu } from "../components/ProductionOrderMenu"
import { HoverActionMenu } from "../../../../../../shared/components/table/HoverActionMenu"
import type { RenderPrintViewOrderMenu } from "../../../components/receiptAndSlip/PrintViewOrderActions"
import { MeetingServiceQuery } from "../../../../../nonAdminUser/reports/home/meetingServiceApi"
import { UserWeekMenu } from "../../../../../nonAdminUser/reports/home/weekMenuApi"
import { OrderChatIcon } from "../../../../../../shared/components/orderChat/OrderChatIcon"
import { OrderChatDrawer } from "../../../../../../shared/components/orderChat/OrderChatDrawer"
import { MeetingServiceOrderDrawer } from "../../../../../../shared/components/meetingServiceOrderDrawer/MeetingServiceOrderDrawer"
import { isOrderRowControlClick } from "../../../../../../shared/components/meetingServiceOrderDrawer/orderDrawerRow"
import { RecurrenceSeriesButton } from "../../../../../../shared/components/recurrenceSeries/RecurrenceSeriesButton"
import { RecurrenceSeriesDrawer } from "../../../../../../shared/components/recurrenceSeries/RecurrenceSeriesDrawer"
import { useOrderDialogUrlState } from "../../../../../../shared/components/createOrder/hooks/useOrderDialogUrlState"
import { CreateOrderDialogMode } from "../../../../../../shared/components/createOrder/createOrderTypes"
import { CreateOrderType } from "../../../../../../shared/components/createOrder/helpers"
import { ChatMessageAuthorRole, UserType } from "../../../../../../shared/components/orderChat/orderChatTypes"
import { TimeOrDatePicker } from "../../../../../../shared/components/table/TimeOrDatePicker"
import { getOrderPerson } from "../../../../../../shared/utils/getOrderPerson"
import {
    StandardSuccessDialog,
    SuccessDialogType,
} from "../../../../../../shared/components/dialogs/StandardSuccessDialog"
import { MerchantHomeQuery } from "../../../home/merchantHomeApi"
import { useCreditNoteDialog } from "../../../../../../shared/components/dialogs/creditNoteDialog/useCreditNoteDialog"
import { DeliveryListMode, OrderDeliveryList } from "../components/OrderDeliveryList"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { Logger } from "../../../../../../lib/logging"
import { useDateTime } from "../../../../../localization/useDateTime"
import { StandardRefreshButton } from "../../../../../../shared/components/table/StandardRefreshButton"
import { RefetchIntervals } from "../../../../../../core/api/refetchIntervals"
import { ErrorDisplay } from "../../../../../../core/api/errorTypes"
import {
    NEW_ITEM_ROW_ATTRIBUTE,
    StandardNewItemsPill,
} from "../../../../../../shared/components/newItems/StandardNewItemsPill"
import { useNewItems } from "../../../../../../shared/components/newItems/useNewItems"

const useStyles = makeStyles()((theme) => ({
    dateFilter: {
        maxWidth: "11rem",
        ".MuiSelect-select": {
            padding: "0.5rem 1rem",
        },
    },
    deliveryTypeFilter: {
        minWidth: "12.5rem",
    },
    tableHeadRow: {
        "& > th.MuiTableCell-root:first-of-type": {
            borderLeft: "4px solid transparent",
        },
    },
    tableBodyRow: {
        cursor: "pointer",
        "& > td:first-of-type": {
            borderLeft: "4px solid transparent",
        },
    },
    tableBodyRowNew: {
        backgroundColor: `${theme.palette.tailwind.sky[50]} !important`,
        "& > td": {
            backgroundColor: `${theme.palette.tailwind.sky[50]} !important`,
        },
    },
    tableBodyRowAccent: {
        "& > td:first-of-type": {
            borderLeftColor: theme.palette.tailwind.amber[400],
        },
    },
    wrapIconText: {
        display: "flex",
        gap: "2px",
        alignItems: "center",
    },
    inlineIconWrapper: {
        display: "inline-block",
        marginLeft: "1rem",
        whiteSpace: "nowrap",
    },
    inlineIcon: {
        verticalAlign: "middle",
        color: "#334155",
    },
    inlineIconText: {
        marginLeft: "0.25rem",
    },
    classNameOverride: {
        paddingBottom: "4rem",
    },
    changeTimeButtonSelect: {
        cursor: "pointer",
        fontSize: "0.875rem",
        marginRight: "2rem",
    },
    table: {
        ".MuiTableCell-root": {
            paddingLeft: "1rem",
            paddingRight: "1rem",
        },
        ".Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
    },
    toolbarSecondRow: {
        alignItems: "center",
        gap: ".5rem",
        padding: "0 1rem 0 1rem",
    },
    filterButtons: {
        paddingTop: 0,
    },
    lockedIcon: {
        color: theme.palette.tailwind.slate[700],
    },
    unlockedIcon: {
        color: theme.palette.tailwind.green[500],
    },
    exportSelectedContainer: {
        width: "7rem",
    },
    rightActionButton: {
        whiteSpace: "nowrap",
    },
}))

const selectMeetingServiceOrders = (filters: MeetingServiceClientSideFilters) => (data: MeetingServiceOrdersResponse) =>
    filterMeetingServiceOrders(data?.orders || [], filters)

const getOrderId = (order: MeetingServiceOrder) => order.id

type DeliveryListState = Readonly<{
    open: boolean
    mode: DeliveryListMode
}>

export enum DialogType {
    BOOK = "book",
    DELETE = "delete",
}
export enum CreatePDFType {
    SLIP = "slip",
    RECEIPT = "receipt",
}
const defaultDialogState = {
    showConfirmDialog: false,
    items: [],
    type: DialogType.BOOK,
}

export type ConfirmationState = {
    showConfirmDialog: boolean
    items: MeetingServiceOrder[]
    type: DialogType
}

export enum MEETING_SERVICE_QUERY_PARAM {
    PERIOD = "period",
    READY_FOR_INVOICING = "readyForInvoicing",
    SHOW_UNAPPROVED_ORDERS = "showUnapprovedOrders",
}

export const statusOrderList = [
    {
        id: 0,
        name: MEETING_SERVICE_QUERY_PARAM.SHOW_UNAPPROVED_ORDERS,
    },
    {
        id: 1,
        name: "isBooked",
    },
]
export type FilterOrdersProps = Readonly<{
    ordersStatus: string[]
    customersId: { customerId: string | undefined; groupId: string | undefined }[]
}>

export type ActionType = "update_meeting_service" | "duplicate_meeting_service"

type MeetingServiceTabProps = {
    tab?: MeetingServiceOrderStatus
    view: MeetingServiceOrdersView
}

export default function MeetingServiceTab({ tab, view }: MeetingServiceTabProps) {
    const { filtersList, setFiltersList, search } = useMeetingServiceFilters()
    const { classes, cx } = useStyles()
    const { classes: gridClasses } = useGridLayoutStyles()
    const { merchantId } = useMerchant()
    const userId = useAppSelector(selectEffectiveUserId)
    const isPrintViewOrderMenuEnabled = useMatchEnvironment(PRINT_CHAT_ENVIRONMENTS)
    const query = useLocationQuery()
    const dateTimeFactory = useDateTime()

    const todayDate = dateTimeFactory.now()
    const queryShowUnapprovedOrders = query.get(MEETING_SERVICE_QUERY_PARAM.SHOW_UNAPPROVED_ORDERS)

    const { l10n } = useAppLocalization()
    const { isDatePicker, timePeriod, changeTimePeriod, changeDateRangeMode } = useProductionPeriodFilter(
        TimePeriodMeetingService.THIS_WEEK,
    )
    const deliveryDateRange = {
        startDate: filtersList.startDeliveryDate ?? todayDate,
        endDate: filtersList.endDeliveryDate ?? todayDate,
    }

    const [deliveryType, setDeliveryType] = useState<DeliveryTypeFilter>(DeliveryTypeFilter.ALL)
    const [cancelResourceBooking, setCancelResourceBooking] = useState<boolean>(false)
    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)
    const [cancellationMessage, setCancellationMessage] = useState<string>("")

    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [sorting, setSorting] = useState<MRT_SortingState>([])
    const [showSuccessModal, setShowSuccessModal] = useState<{
        open: boolean
        type?: SuccessDialogType
        ordersCount?: number
        closeCallback?: () => void
    } | null>(null)

    const queryClient = useQueryClient()

    const [selectedRows, setSelectedRows] = useState<MeetingServiceOrder[]>()
    const [activeRow, setActiveRow] = useState<MeetingServiceOrder>()
    const [drawerOrderId, setDrawerOrderId] = useState<number>()
    const [seriesOrder, setSeriesOrder] = useState<MeetingServiceOrder>()
    const orderIds = selectedRows ? selectedRows.map((order) => order.id) : []
    const [receiptModal, setReceiptModal] = useState<{
        open: boolean
        activeOrderId?: number
        activeOrder?: MeetingServiceOrder
        orders: MeetingServiceOrder[]
        singleMode?: boolean
    }>({
        open: false,
        orders: [],
        singleMode: true,
    })
    const [showPackSlipModal, setShowPackSlipModal] = useState(false)
    const [packSlipSingleOrder, setPackSlipSingleOrder] = useState<MeetingServiceOrder>()
    const [selectedStatus, setSelectedStatus] = useState<FilteredStatuses | undefined>()
    const [showStatusChangeModal, setShowStatusChangeModal] = useState(false)
    const [deliveryListState, setDeliveryListState] = useState<DeliveryListState>({
        open: false,
        mode: "selected",
    })

    const [slipActiveOrder, setSlipActiveOrder] = useState<MeetingServiceOrder>()
    const [chatDialogOrder, setChatDialogOrder] = useState<{ id: number; title: string } | null>(null)

    const tableInstanceRef = useRef<MRT_TableInstance<MeetingServiceOrder>>(null)
    const tableContainerRef = useRef<HTMLDivElement>(null)
    const [confirmDialogState, setConfirmDialogState] = useState<ConfirmationState>({ ...defaultDialogState })

    const excludeDateTime = [MeetingServiceOrderStatus.NOT_APPROVED, MeetingServiceOrderStatus.TO_BE_BOOKED].some(
        (t) => t === tab,
    )

    useEffect(() => {
        const queryPeriod = query.get(MEETING_SERVICE_QUERY_PARAM.PERIOD)
        if (queryPeriod) {
            const normalizedParam = queryPeriod.toUpperCase().replace(/ /g, "_")
            const periodValue = Object.values(TimePeriodMeetingService).find((value) => value === normalizedParam)
            if (periodValue) {
                changeTimePeriod(periodValue)
                changeDateRangeMode(false, deliveryDateRange)
            }
        }
    }, [query.get(MEETING_SERVICE_QUERY_PARAM.PERIOD)])

    useEffect(() => {
        !!queryShowUnapprovedOrders &&
            setFiltersList((prev) => {
                return {
                    ...prev,
                    ordersStatus: ["0"],
                }
            })
    }, [])

    // a search or an order id is for an order whose date is unknown, so the period must not narrow it
    const searchQuery = search.length >= ORDERS_SEARCH_MIN_LENGTH ? search : undefined
    const isSearchingAllDates = Boolean(filtersList.orderId) || Boolean(searchQuery)

    const periodDependencies = useMemo(() => {
        const {
            // Exclude these from rest - they are handled explicitly or not needed
            startBookedDate: _startBookedDate,
            endBookedDate: _endBookedDate,
            startDeliveryDate: _startDeliveryDate,
            endDeliveryDate: _endDeliveryDate,
            pendingApproval: _pendingApproval,
            invoiceStatus: _invoiceStatus,
            isBooked: _isBooked,
            ...restFilters
        } = filtersList

        return {
            ...restFilters,
            merchantId,
            view,
            deliveryType,
            period:
                isDatePicker || timePeriod === TimePeriodMeetingService.ALL ? TimePeriodMeetingService.ALL : timePeriod,
            startDeliveryDate:
                isDatePicker && !excludeDateTime
                    ? (filtersList.startDeliveryDate?.toISODate() ?? undefined)
                    : undefined,
            endDeliveryDate:
                isDatePicker && !excludeDateTime ? (filtersList.endDeliveryDate?.toISODate() ?? undefined) : undefined,
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            satelliteIds: filtersList.satelliteIds?.join(","),
            buyerPartyIds: filtersList.buyerPartyIds?.join(","),
            paymentMethod: filtersList.paymentMethod?.join(","),
        }
    }, [filtersList, merchantId, deliveryType, isDatePicker, timePeriod, excludeDateTime, view])

    const meetingServiceDependencies = useMemo(
        () =>
            isSearchingAllDates
                ? {
                      ...periodDependencies,
                      search: searchQuery,
                      period: TimePeriodMeetingService.ALL,
                      startDeliveryDate: undefined,
                      endDeliveryDate: undefined,
                  }
                : periodDependencies,
        [periodDependencies, isSearchingAllDates, searchQuery],
    )

    // Only the end-user filters are exposed here; the delivery dates in filtersList drive the date-picker mode
    // and are already applied through the period/date params, so they must not narrow the rows again.
    const selectMeetingService = useMemo(
        () => selectMeetingServiceOrders({ email: filtersList.email, employeeId: filtersList.employeeId }),
        [filtersList.email, filtersList.employeeId],
    )

    const meetingServiceResult = useApiQuery(fetchMeetingServiceOrders, {
        queryName: MerchantMeetingServiceOrdersQuery.MEETING_SERVICE,
        dependencies: meetingServiceDependencies,
        select: selectMeetingService,
        refetchInterval: RefetchIntervals.medium,
        errorDisplay: ErrorDisplay.NONE,
        keepPreviousData: isSearchingAllDates,
    })

    const allowedOrderIds = useMemo(() => {
        if (!selectedStatus || (!selectedRows && !activeRow)) return []
        const rows = activeRow ? [activeRow] : selectedRows || []
        const allowedIds = rows
            ?.filter(
                (order) =>
                    order.orderAction.statusTransitions?.allowed?.[selectedStatus] ||
                    !order.orderAction.statusTransitions?.disallowed?.[selectedStatus],
            )
            .map((order) => order.id)

        const logger = new Logger("meeting-service")
        logger.info(
            `Calculated allowed order ids [selected rows: ${rows.length}, allowed order ids: ${allowedIds.length}]`,
        )

        return allowedIds
    }, [selectedStatus, selectedRows, activeRow])

    const { creditNoteDialog, openCreditNoteDialog } = useCreditNoteDialog()

    const { newIds, highlightedIds, acknowledge } = useNewItems(
        meetingServiceResult.data,
        getOrderId,
        meetingServiceDependencies,
    )

    const { ApiQueryTable, columnFactory } = useApiQueryTable(meetingServiceResult)
    const drawerOrder =
        drawerOrderId != null ? meetingServiceResult.data?.find((order) => order.id === drawerOrderId) : undefined

    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    // row ids are indexes, so a new data set would point the selection at other orders
    useEffect(() => {
        setRowSelection({})
    }, [meetingServiceDependencies])

    const deleteCallback = () => {
        queryClient.invalidateQueries({
            queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
        })
        queryClient.invalidateQueries({
            queryKey: [MerchantHomeQuery.MEETING_SERVICE_ORDER_SUMMARY_STATUS],
        })
        queryClient.invalidateQueries({ queryKey: [MerchantMeetingServiceOrdersQuery.ORDERS_FOR_EXPORT] })
        queryClient.invalidateQueries({ queryKey: [MerchantMeetingServiceOrdersQuery.RECEIPT_ORDERS_FOR_EXPORT] })
        queryClient.invalidateQueries({ queryKey: [MeetingServiceQuery.ORDER_DETAILS] })
        queryClient.invalidateQueries({ queryKey: [UserWeekMenu.USER_MEETING_SERVICE_DETAILS] })
    }

    const { mutate: callChangeBookStatus } = useApiMutation(changeBookStatus, {
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
            })
        },
    })

    const { mutate: callDeleteOrders, isLoading: isDeletingOrders } = useApiMutation(deleteMeetingServiceOrders, {
        async onSuccess(params, _data) {
            handleCloseReceipt()
            handleClosePackSlip()
            if (deliveryListState.mode === "selected") handleCloseDeliveryList()
            handleClearSelected()
            setCancellationMessage("")
            setShowSuccessModal({
                open: true,
                type: "deleteOrder",
                ordersCount: params.orderIds?.length ?? 0,
                closeCallback: deleteCallback,
            })
        },
    })
    const showStatusUndo = useMeetingServiceStatusUndo()
    const { openDialog } = useOrderDialogUrlState()

    const { mutate: callChangeLockStatus } = useApiMutation(changeLockStatusMerchant, {
        async onMutate(params) {
            const previousData = queryClient.getQueryData<MeetingServiceOrdersResponse>([
                MerchantMeetingServiceOrdersQuery.MEETING_SERVICE,
                meetingServiceDependencies,
            ])?.orders

            queryClient.setQueryData<MeetingServiceOrdersResponse>(
                [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE, meetingServiceDependencies],
                (old) => {
                    if (!old) return old
                    return {
                        ...old,
                        orders: old.orders.map((order) =>
                            params.updateLockStatus.orderIds.includes(order.id)
                                ? {
                                      ...order,
                                      deliveryDetails: {
                                          ...order.deliveryDetails,
                                          isLocked: params.updateLockStatus.isLocked,
                                      },
                                  }
                                : order,
                        ),
                    }
                },
            )

            return { previousData }
        },
        async onError(_err, _params, context) {
            if ("previousData" in context && context.previousData) {
                context.queryClient.setQueryData<MeetingServiceOrdersResponse>(
                    [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE, meetingServiceDependencies],
                    (old) => {
                        if (!old) return old
                        return {
                            ...old,
                            orders: context.previousData!,
                        }
                    },
                )
            }
        },
    })

    const handleLockStatusChange = useCallback(
        (orderIds: number[], isLocked: boolean) => {
            if (!orderIds.length) return
            callChangeLockStatus({
                updateLockStatus: {
                    isLocked,
                    orderIds,
                },
            })
        },
        [callChangeLockStatus],
    )

    const onStatusClick =
        (placement: "top" | "bottom") => (e: React.MouseEvent<HTMLElement>, row?: MeetingServiceOrder) => {
            if (menuPos?.left || menuPos?.top) {
                setMenuPos(null)
                setActiveRow(undefined)
            } else {
                const rect = e.currentTarget.getBoundingClientRect()
                setActiveRow(row)
                setMenuPos((prev) =>
                    prev
                        ? null
                        : placement === "bottom"
                          ? { top: rect.bottom, left: rect.right }
                          : { top: rect.top, left: rect.right },
                )
            }
        }

    const columns = useMemo<MRT_ColumnDef<MeetingServiceOrder>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("meeting-service-table-order-id-column"),
                accessorKey: "id",
                size: 150,
                minSize: 150,
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("meeting-service-table-delivery-time-column"),
                    accessorKey: "deliveryDetails.time",
                    size: 80,
                },
                (row) => row?.deliveryDetails.time,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("meeting-service-table-delivery-location-column"),
                accessorKey: "deliveryDetails",
                size: COLUMN_SIZE.L,
                muiTableHeadCellProps: { className: GROW_CELL_CLASS },
                muiTableBodyCellProps: { className: GROW_CELL_CLASS },
                Cell: ({ row }) => {
                    const { id, meetingDetails, deliveryDetails, organizers, messages, paymentDetails } = row.original
                    const deliveryTypeValue = deliveryDetails.type
                    const { responsible } = getOrderPerson(organizers)

                    const meetingStart = dateTimeFactory.fromApiOrUndefined(meetingDetails.startDateTime)
                    const meetingEnd = dateTimeFactory.fromApiOrUndefined(meetingDetails.endDateTime)
                    const meetingTime =
                        meetingStart?.isValid && meetingEnd?.isValid
                            ? `${dateTimeFactory.formatTime(meetingStart)} - ${dateTimeFactory.formatTime(meetingEnd)}`
                            : undefined

                    return (
                        <StandardTwoLinesCell
                            title={
                                <Stack direction="row" alignItems="center" gap="1rem" component="span">
                                    <span>
                                        {meetingDetails.meetingName}
                                        {meetingDetails.isRecurrence && (
                                            <RecurrenceSeriesButton
                                                count={meetingDetails.recurrence?.orders?.length}
                                                onClick={() => setSeriesOrder(row.original)}
                                            />
                                        )}
                                        {!!deliveryDetails.sittings && (
                                            <span className={cx(classes.wrapIconText, classes.inlineIconWrapper)}>
                                                <FontAwesomeIcon icon={faUser} className={classes.inlineIcon} />
                                                <span className={classes.inlineIconText}>
                                                    {deliveryDetails.sittings}
                                                </span>
                                            </span>
                                        )}
                                    </span>
                                    <OrderChatIcon
                                        messages={messages}
                                        onClick={() => setChatDialogOrder({ id, title: meetingDetails.meetingName })}
                                    />
                                    {paymentDetails?.isContantPayment && (
                                        <FontAwesomeIcon icon={faCircleEuro} color={"#334155"} />
                                    )}
                                </Stack>
                            }
                            subtitle1={
                                deliveryTypeValue === DeliveryType.DELIVERY ? (
                                    <LocalizedStrict
                                        id="meeting-service-table-meeting"
                                        vars={{ locationName: deliveryDetails.location.name }}
                                    >
                                        <span>{`Delivery: ${deliveryDetails.location.name}`}</span>
                                    </LocalizedStrict>
                                ) : (
                                    <LocalizedEnum base="delivery-type-filter" enumValue={deliveryDetails.type}>
                                        <span>{deliveryTypeValue}</span>
                                    </LocalizedEnum>
                                )
                            }
                            subtitle2={meetingTime}
                            subtitle3={responsible?.name}
                        />
                    )
                },
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("meeting-service-table-amount-column"),
                    id: "amount",
                    size: 180,
                    minSize: 180,
                },
                (row) => row.amount,
                true,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("meeting-service-table-lock-column"),
                accessorKey: "deliveryDetails.isLocked",
                size: 120,
                enableSorting: false,
                Cell: ({ row }) => {
                    const isLocked = Boolean(row.original.deliveryDetails.isLocked)
                    const tooltipId = isLocked
                        ? "meeting-service-table-lock-column-lock-tooltip"
                        : "meeting-service-table-lock-column-unlock-tooltip"

                    return (
                        <LocalizedStrict id={tooltipId} attrs={{ title: true }}>
                            <Tooltip arrow title={l10n.getString(tooltipId)}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleLockStatusChange([row.original.id], !isLocked)}
                                >
                                    <FontAwesomeIcon
                                        icon={isLocked ? faLock : faLockOpen}
                                        className={isLocked ? classes.lockedIcon : classes.unlockedIcon}
                                    />
                                </IconButton>
                            </Tooltip>
                        </LocalizedStrict>
                    )
                },
            }),
            {
                // statusColumn() hard-pins width to COLUMN_SIZE.SM (160) internally, which
                // crops the interactive status button + chevron in grid mode. Override the
                // size after the factory so it actually applies.
                ...columnFactory.statusColumn({
                    id: "status",
                    header: l10n.getString("service-center-admin-organization-resources-table-header-status"),
                    accessorFn: (row) => row.deliveryDetails.statusDetails.status,
                    Cell: ({ row }) => {
                        return (
                            <MinimizedStatusButton
                                onClick={(e) => onStatusClick("bottom")(e, row.original)}
                                status={row.original.deliveryDetails.statusDetails.status}
                                tooltipLabel={row.original.deliveryDetails.statusDetails.message}
                            />
                        )
                    },
                }),
                size: COLUMN_SIZE.M,
                minSize: COLUMN_SIZE.M,
                maxSize: COLUMN_SIZE.M,
            },
        ],
        [l10n, columnFactory, handleLockStatusChange, dateTimeFactory],
    )

    const handleChangeTimeOrDatePicker = () => changeDateRangeMode(!isDatePicker, deliveryDateRange)

    const handleChangeDeliveryType = (event: any) => {
        setDeliveryType(event.target.value)
    }

    const selectedDeliveryRange = isSearchingAllDates ? getOrdersDeliveryRange(selectedRows ?? []) : undefined

    const freshSelectedRows = useMemo(
        () => selectedRows && withFreshRows(selectedRows, meetingServiceResult.data),
        [selectedRows, meetingServiceResult.data],
    )

    const exportSelectedOrders = useExportMeetingServiceOrdersToCsv({
        data: freshSelectedRows,
        startDate:
            selectedDeliveryRange?.start ??
            (isDatePicker
                ? (filtersList.startDeliveryDate ?? todayDate)
                : (getTimePeriod(dateTimeFactory, timePeriod)?.start ?? todayDate)),
        endDate:
            selectedDeliveryRange?.end ??
            (isDatePicker
                ? (filtersList.endDeliveryDate ?? todayDate)
                : (getTimePeriod(dateTimeFactory, timePeriod)?.end ?? todayDate)),
        includeVatInLabels: true,
    })
    const { exportFresh: exportFreshSelectedOrders, isExporting: isExportingSelectedOrders } = useRefreshBeforeExport(
        meetingServiceResult,
        exportSelectedOrders,
    )

    const handleClearSelected = () => {
        setSelectedRows(undefined)
        setRowSelection({})
    }

    const handleCloseReceipt = () => {
        setReceiptModal({ open: false, orders: [] })
    }

    const handleClosePackSlip = () => {
        setShowPackSlipModal(false)
        setPackSlipSingleOrder(undefined)
    }

    const handleCloseBatchModal = () => {
        setShowStatusChangeModal(false)
        handleClearSelected()
    }

    const onStatusUpdate = (status: FilteredStatuses) => {
        setSelectedStatus(status)
        setShowStatusChangeModal(true)
    }

    const setShowUndo = (orderCount: number) =>
        showStatusUndo({
            status: selectedStatus!,
            orderIds,
            orderCount,
            onUndone: () => setSelectedStatus(undefined),
            onSettled: () => setSelectedStatus(undefined),
        })

    const handleStatusClose = () => setMenuPos(null)

    const handleCloseDeliveryList = () => {
        setDeliveryListState((prev) => ({ ...prev, open: false }))
    }

    const closePrintViews = () => {
        handleCloseReceipt()
        handleClosePackSlip()
        handleCloseDeliveryList()
    }

    const getPrintViewOrders = (order: MeetingServiceOrder) => {
        const tableOrders = getFilteredTableData(tableInstanceRef)
        return tableOrders.some(({ id }) => id === order.id) ? tableOrders : [order]
    }

    const switchPrintViewToReceipt = (order: MeetingServiceOrder) => {
        closePrintViews()
        setReceiptModal({
            open: true,
            activeOrderId: order.id,
            activeOrder: order,
            orders: getPrintViewOrders(order),
            singleMode: true,
        })
    }

    const switchPrintViewToPackSlip = (order: MeetingServiceOrder) => {
        closePrintViews()
        setSlipActiveOrder(order)
        setPackSlipSingleOrder(order)
        setShowPackSlipModal(true)
    }

    const renderOrderMenu = (
        row: MeetingServiceOrder,
        closeMenu: () => void,
        overrides?: Partial<ProductionOrderMenuContentProps>,
    ) => (
        <ProductionOrderMenu
            key={row.id}
            row={row}
            openCreditNoteDialog={openCreditNoteDialog}
            setShowReceiptModal={(id: number) => {
                setReceiptModal({
                    open: true,
                    activeOrderId: id,
                    activeOrder: row,
                    orders: tableInstanceRef.current?.getPrePaginationRowModel().rows.map((row) => row.original) ?? [],
                    singleMode: true,
                })
                closeMenu()
            }}
            handleMenuClose={closeMenu}
            setConfirmDialogState={setConfirmDialogState}
            menuItemClick={() => {
                setSlipActiveOrder(row)
                closeMenu()
            }}
            setShowPackSlipModal={setShowPackSlipModal}
            onChatOpen={(id, title) => {
                setChatDialogOrder({ id, title })
                closeMenu()
            }}
            {...overrides}
        />
    )

    const renderPrintViewOrderMenu =
        (
            viewOrders: ReadonlyArray<MeetingServiceOrder>,
            overrides?: Partial<ProductionOrderMenuContentProps>,
        ): RenderPrintViewOrderMenu =>
        (orderId, printActions, order) => {
            const row = order ?? viewOrders.find((viewOrder) => viewOrder.id === orderId)
            if (!row) return null

            return (
                <HoverActionMenu>
                    {(closeMenu) =>
                        renderOrderMenu(row, closeMenu, {
                            setShowReceiptModal: () => switchPrintViewToReceipt(row),
                            setShowPackSlipModal: () => switchPrintViewToPackSlip(row),
                            ...overrides,
                            ...printActions,
                        })
                    }
                </HoverActionMenu>
            )
        }

    const packSlipOrders = packSlipSingleOrder
        ? getPrintViewOrders(packSlipSingleOrder)
        : getFilteredTableData(tableInstanceRef)

    return (
        <Stack gap="2rem">
            <StatusButton
                placement={Boolean(selectedRows?.length) ? "top" : "bottom"}
                hiddenStatuses={getHiddenOrderStatuses(view)}
                onStatusChange={onStatusUpdate}
                anchorEl={menuPos ?? undefined}
                handleClose={handleStatusClose}
                currentStatus={activeRow?.deliveryDetails.statusDetails.status}
            />

            {showSuccessModal?.open && (
                <StandardSuccessDialog
                    dialogType={showSuccessModal?.type}
                    open={showSuccessModal.open || true}
                    ordersCount={showSuccessModal?.ordersCount}
                    onClose={() => {
                        showSuccessModal?.closeCallback?.()
                        setShowSuccessModal({ open: false })
                    }}
                />
            )}

            {creditNoteDialog}

            {receiptModal.open && (
                <UserMeetingsReceipt
                    open={receiptModal.open}
                    onClose={handleCloseReceipt}
                    singleMode={receiptModal.singleMode}
                    meetings={receiptModal.orders}
                    id={receiptModal.activeOrderId}
                    activeMeeting={receiptModal.activeOrder}
                    renderOrderMenu={
                        isPrintViewOrderMenuEnabled
                            ? renderPrintViewOrderMenu(receiptModal.orders, { setShowReceiptModal: undefined })
                            : undefined
                    }
                />
            )}
            <Box ref={tableContainerRef} className={classes.table}>
                <ApiQueryTable
                    layoutMode="grid" // or "semantic"
                    muiTablePaperProps={{ className: gridClasses.pinned }}
                    enableColumnResizing={false}
                    tableInstanceRef={tableInstanceRef}
                    enableRowSelection
                    columns={columns}
                    onRowSelectionChange={setRowSelection}
                    initialState={{ density: "spacious" }}
                    muiTableBodyRowProps={(row) => {
                        const isBooked = row.row.original?.accountingDetails?.isBooked
                        const isNew = highlightedIds.has(row.row.original.id)

                        return {
                            [NEW_ITEM_ROW_ATTRIBUTE]: isNew,
                            className: cx(classes.tableBodyRow, {
                                [classes.tableBodyRowAccent]: isBooked,
                                [classes.tableBodyRowNew]: isNew,
                            }),
                            onClick: (event) => {
                                if (isOrderRowControlClick(event)) return
                                setDrawerOrderId(row.row.original.id)
                            },
                        }
                    }}
                    muiTableHeadRowProps={{ className: classes.tableHeadRow }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [renderOrderMenu(row.original, closeMenu)]}
                    renderTopToolbarCustomActions={() => (
                        <Stack>
                            <StandardTableFilter variant="left">
                                {!excludeDateTime && (
                                    <TimeOrDatePicker<TimePeriodMeetingService>
                                        excludeDateTime={excludeDateTime}
                                        disabled={isSearchingAllDates}
                                        isDatePicker={isDatePicker}
                                        dateState={{
                                            startDate: filtersList.startDeliveryDate!, // as we set date by default it's always defined
                                            endDate: filtersList.endDeliveryDate!,
                                        }}
                                        setDateState={(newValue) => {
                                            setFiltersList((prev) => ({
                                                ...prev,
                                                startDeliveryDate: newValue.startDate,
                                                endDeliveryDate: newValue.endDate,
                                            }))
                                        }}
                                        timePeriodOptions={[...Object.values(TimePeriodMeetingService)]}
                                        timePeriod={timePeriod}
                                        handleChangeTimePeriod={changeTimePeriod}
                                        handleChangeTimeOrDatePicker={handleChangeTimeOrDatePicker}
                                    />
                                )}

                                <FormControl fullWidth className={cx(classes.dateFilter, classes.deliveryTypeFilter)}>
                                    <InputLabel id="time-period-select-label">
                                        <LocalizedStrict id="meeting-service-table-delivery-type">
                                            <>Delivery type</>
                                        </LocalizedStrict>
                                    </InputLabel>
                                    <Select
                                        labelId="time-period-select-label"
                                        id="time-period-select"
                                        value={deliveryType}
                                        label="Delivery type"
                                        onChange={handleChangeDeliveryType}
                                    >
                                        {Object.entries(DeliveryTypeFilter).map(([key, value]) => (
                                            <MenuItem key={key} value={value}>
                                                <LocalizedEnum base="delivery-type-filter" enumValue={value}>
                                                    <>{value}</>
                                                </LocalizedEnum>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </StandardTableFilter>
                            <Stack direction="row" className={classes.toolbarSecondRow}>
                                <StandardRefreshButton query={meetingServiceResult} bordered />
                                <StandardNewItemsPill
                                    count={newIds.size}
                                    onAcknowledge={acknowledge}
                                    containerRef={tableContainerRef}
                                />
                            </Stack>
                        </Stack>
                    )}
                    renderToolbarInternalActions={({ table }) => (
                        <StandardTableFilter
                            stackClassName={classes.filterButtons}
                            variant="right"
                            table={table}
                            showGlobalFilterButton={false}
                        >
                            <LocalizedStrict id="meeting-service-delivery-list">
                                <StandardButton
                                    fullWidth={false}
                                    size="small"
                                    className={classes.rightActionButton}
                                    variant="outlined"
                                    onClick={() => {
                                        setDeliveryListState({ open: true, mode: "all" })
                                    }}
                                    startIcon={<FontAwesomeIcon icon={faPrint} />}
                                >
                                    Delivery list
                                </StandardButton>
                            </LocalizedStrict>
                        </StandardTableFilter>
                    )}
                    renderBottomToolbarCustomActions={({ table }) => {
                        return (
                            <TableSummaryWrap>
                                <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                                    <TableNumberOfItemsBox
                                        data={table.getFilteredRowModel().rows}
                                        isLoading={meetingServiceResult.isLoading}
                                        title="Number of orders"
                                    />
                                </LocalizedStrict>

                                <TableTotalAmountBox
                                    data={table.getFilteredRowModel().rows}
                                    isLoading={meetingServiceResult.isLoading}
                                    accessor={(row) => row.original.amount}
                                />
                            </TableSummaryWrap>
                        )
                    }}
                    state={{
                        rowSelection,
                        sorting,
                    }}
                    onSortingChange={setSorting}
                />
            </Box>
            {showPackSlipModal && (
                <OrderPackageSlip
                    orders={packSlipOrders}
                    onClose={handleClosePackSlip}
                    merchantId={merchantId}
                    id={slipActiveOrder?.id}
                    activeOrder={slipActiveOrder}
                    orderIds={packSlipSingleOrder ? undefined : orderIds}
                    open={showPackSlipModal}
                    renderOrderMenu={
                        isPrintViewOrderMenuEnabled
                            ? renderPrintViewOrderMenu(packSlipOrders, { setShowPackSlipModal: undefined })
                            : undefined
                    }
                />
            )}
            {showStatusChangeModal && (
                <LocalizedStrict id="meeting-service-batch-modal-title" attrs={{ dialogTitle: true }}>
                    <StandardDialog
                        open={showStatusChangeModal}
                        onClose={handleCloseBatchModal}
                        dialogTitle="Change status"
                        maxWidthProp="lg"
                    >
                        <StatusChangeModal
                            setShowUndo={setShowUndo}
                            onClose={handleCloseBatchModal}
                            selectedStatus={selectedStatus!}
                            allowedOrderIds={allowedOrderIds}
                            orders={activeRow ? [activeRow] : selectedRows!}
                            meetingServiceDependencies={meetingServiceDependencies}
                        />
                    </StandardDialog>
                </LocalizedStrict>
            )}
            {deliveryListState.open && (
                <OrderDeliveryList
                    open={deliveryListState.open}
                    onClose={handleCloseDeliveryList}
                    orderIds={orderIds}
                    mode={deliveryListState.mode}
                    meetingServiceDependencies={periodDependencies}
                    tab={tab}
                    renderOrderMenu={
                        isPrintViewOrderMenuEnabled
                            ? renderPrintViewOrderMenu(meetingServiceResult.data ?? [])
                            : undefined
                    }
                />
            )}
            {selectedRows && (
                <FloatingActionBar selectedItemsLength={selectedRows.length} handleCloseActionBar={handleClearSelected}>
                    <LocalizedStrict id="meeting-service-table-floating-action-bar-status" attrs={{ title: true }}>
                        <ActionBarItem icon={faArrowProgress} title="Status" onClick={onStatusClick("top")} />
                    </LocalizedStrict>

                    <LocalizedStrict id="meeting-service-table-floating-action-bar-lock-unlock" attrs={{ title: true }}>
                        <ActionBarMenu icon={faLock} title={l10n.getString("meeting-service-table-lock-column")}>
                            <LocalizedStrict
                                id="meeting-service-table-floating-action-bar-lock-tooltip"
                                attrs={{ title: true }}
                            >
                                <Tooltip
                                    arrow
                                    title={l10n.getString("meeting-service-table-floating-action-bar-lock-tooltip")}
                                >
                                    <Box>
                                        <LocalizedStrict
                                            id="meeting-service-table-floating-action-bar-lock"
                                            attrs={{ title: true }}
                                        >
                                            <ActionBarMenuItem
                                                icon={faLock}
                                                title="Lock"
                                                onClick={() => handleLockStatusChange(orderIds, true)}
                                            />
                                        </LocalizedStrict>
                                    </Box>
                                </Tooltip>
                            </LocalizedStrict>
                            <LocalizedStrict
                                id="meeting-service-table-floating-action-bar-unlock-tooltip"
                                attrs={{ title: true }}
                            >
                                <Tooltip
                                    arrow
                                    title={l10n.getString("meeting-service-table-floating-action-bar-unlock-tooltip")}
                                >
                                    <Box>
                                        <LocalizedStrict
                                            id="meeting-service-table-floating-action-bar-unlock"
                                            attrs={{ title: true }}
                                        >
                                            <ActionBarMenuItem
                                                icon={faLockOpen}
                                                title="Unlock"
                                                onClick={() => handleLockStatusChange(orderIds, false)}
                                            />
                                        </LocalizedStrict>
                                    </Box>
                                </Tooltip>
                            </LocalizedStrict>
                        </ActionBarMenu>
                    </LocalizedStrict>

                    <LocalizedStrict id="meeting-service-table-floating-action-bar-delete" attrs={{ title: true }}>
                        <ActionBarItem
                            icon={faTrash}
                            title="Delete"
                            onClick={() =>
                                setConfirmDialogState({
                                    showConfirmDialog: true,
                                    items: selectedRows,
                                    type: DialogType.DELETE,
                                })
                            }
                        />
                    </LocalizedStrict>

                    <LocalizedStrict id="meeting-service-table-floating-action-bar-print" attrs={{ title: true }}>
                        <ActionBarMenu icon={faPrint} title="Print">
                            <LocalizedStrict
                                id="meeting-service-table-floating-action-bar-delivery-list"
                                attrs={{ title: true }}
                            >
                                <ActionBarMenuItem
                                    icon={faList}
                                    title="Delivery list"
                                    onClick={() => {
                                        setDeliveryListState({ open: true, mode: "selected" })
                                    }}
                                />
                            </LocalizedStrict>
                            <LocalizedStrict
                                id="meeting-service-table-floating-action-bar-production-slip"
                                attrs={{ title: true }}
                            >
                                <ActionBarMenuItem
                                    icon={faList}
                                    title="Packing slip"
                                    onClick={() => {
                                        setShowPackSlipModal(true)
                                    }}
                                />
                            </LocalizedStrict>
                            <LocalizedStrict
                                id="meeting-service-table-floating-action-bar-receipt"
                                attrs={{ title: true }}
                            >
                                <ActionBarMenuItem
                                    icon={faReceipt}
                                    title="Receipt"
                                    onClick={() => {
                                        setReceiptModal({ open: true, orders: selectedRows, singleMode: false })
                                    }}
                                />
                            </LocalizedStrict>
                        </ActionBarMenu>
                    </LocalizedStrict>

                    <LocalizedStrict id="meeting-service-table-floating-action-bar-export" attrs={{ title: true }}>
                        <ActionBarItem
                            icon={faFileArrowDown}
                            title="Export selected"
                            onClick={exportFreshSelectedOrders}
                            isLoading={isExportingSelectedOrders}
                            containerStyle={classes.exportSelectedContainer}
                        />
                    </LocalizedStrict>
                </FloatingActionBar>
            )}
            {confirmDialogState.showConfirmDialog &&
                (confirmDialogState.type === DialogType.DELETE ? (
                    <ConfirmModal
                        hideCloseIcon={false}
                        open={confirmDialogState.showConfirmDialog}
                        title={l10n.getString("meeting-service-delete-orders-title")}
                        buttonLabelSubmit={l10n.getString("meeting-service-delete-orders-button")}
                        buttonSubmitLoading={isDeletingOrders}
                        handleClick={() => {
                            const ids = confirmDialogState.items.map((order) => order.id)
                            callDeleteOrders({
                                orderIds: ids,
                                cancelResourceBooking,
                                cancelMeetingService: true,
                                message: cancellationMessage,
                            })
                        }}
                        onClose={() => {
                            setConfirmDialogState(defaultDialogState)
                            setCancellationMessage("")
                        }}
                        maxWidthProp="lg"
                        classNameOverride={classes.classNameOverride}
                    >
                        <MeetingServiceDeleteDialogContent
                            hasDeleteMessage
                            list={confirmDialogState.items}
                            checked={cancelResourceBooking}
                            cancellationMessage={cancellationMessage}
                            hideBooking
                            setCancellationMessage={setCancellationMessage}
                            handleChange={() => setCancelResourceBooking((prev) => !prev)}
                        />
                    </ConfirmModal>
                ) : (
                    <ConfirmModal
                        open={confirmDialogState.showConfirmDialog}
                        title={
                            <LocalizedStrict id="meeting-service-book-orders-title">
                                <span>Book orders</span>
                            </LocalizedStrict>
                        }
                        buttonLabelSubmit={l10n.getString("meeting-service-book-orders-button")}
                        handleClick={() => {
                            const list = confirmDialogState.items.map((order) => {
                                return {
                                    id: order.id,
                                    ...(order.id ? { cashPaymentDate: "2024-02-22" } : {}),
                                }
                            })
                            callChangeBookStatus({ orderIds: list })
                        }}
                        onClose={() => setConfirmDialogState(defaultDialogState)}
                        maxWidthProp="lg"
                        classNameOverride={classes.classNameOverride}
                    >
                        <MeetingServiceBookDialogContent list={confirmDialogState.items} />
                    </ConfirmModal>
                ))}

            {chatDialogOrder !== null && (
                <OrderChatDrawer
                    open
                    raised
                    onClose={() => setChatDialogOrder(null)}
                    orderId={chatDialogOrder.id}
                    orderTitle={chatDialogOrder.title}
                    viewerRole={ChatMessageAuthorRole.MERCHANT}
                    sender={userId ? { type: UserType.MERCHANT, merchantId, userId } : undefined}
                    onMessageSent={() =>
                        queryClient.invalidateQueries({ queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE] })
                    }
                    onSubtitleClick={() => {
                        setReceiptModal({
                            open: true,
                            activeOrderId: chatDialogOrder.id,
                            orders:
                                tableInstanceRef.current?.getPrePaginationRowModel().rows.map((row) => row.original) ??
                                [],
                            singleMode: true,
                        })
                    }}
                />
            )}

            {seriesOrder && (
                <RecurrenceSeriesDrawer
                    open
                    order={seriesOrder}
                    onOpenOrder={setDrawerOrderId}
                    orderActions={{
                        onEditOrder: (orderId) =>
                            openDialog({
                                orderId,
                                mode: CreateOrderDialogMode.EDIT,
                                activeOrderTab: CreateOrderType.MEETING_SERVICE,
                            }),
                        onCopyOrder: (orderId) =>
                            openDialog({
                                orderId,
                                mode: CreateOrderDialogMode.DUPLICATE,
                                activeOrderTab: CreateOrderType.MEETING_SERVICE,
                            }),
                        getPrintAction: (orderId) => {
                            const listOrder = meetingServiceResult.data?.find((order) => order.id === orderId)
                            if (!listOrder) return undefined

                            return () => {
                                setSlipActiveOrder(listOrder)
                                setShowPackSlipModal(true)
                            }
                        },
                    }}
                    onClose={() => setSeriesOrder(undefined)}
                />
            )}

            {seriesOrder && drawerOrderId != null && !drawerOrder && (
                <MeetingServiceOrderDrawer
                    open
                    order={{ id: drawerOrderId, meetingDetails: seriesOrder.meetingDetails }}
                    viewerRole={ChatMessageAuthorRole.MERCHANT}
                    sender={userId ? { type: UserType.MERCHANT, merchantId, userId } : undefined}
                    onClose={() => setDrawerOrderId(undefined)}
                />
            )}

            {drawerOrder && (
                <MeetingServiceOrderDrawer
                    open
                    order={drawerOrder}
                    viewerRole={ChatMessageAuthorRole.MERCHANT}
                    sender={userId ? { type: UserType.MERCHANT, merchantId, userId } : undefined}
                    onClose={() => setDrawerOrderId(undefined)}
                    merchant={{
                        order: drawerOrder,
                        view,
                        meetingServiceDependencies,
                        onSaved: () =>
                            queryClient.invalidateQueries({
                                queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
                            }),
                        onPackingSlip: () => {
                            setSlipActiveOrder(drawerOrder)
                            setShowPackSlipModal(true)
                        },
                        onReceipt: () =>
                            setReceiptModal({
                                open: true,
                                activeOrderId: drawerOrder.id,
                                activeOrder: drawerOrder,
                                orders:
                                    tableInstanceRef.current
                                        ?.getPrePaginationRowModel()
                                        .rows.map((row) => row.original) ?? [],
                                singleMode: true,
                            }),
                        onFullEdit: () =>
                            openDialog({
                                orderId: drawerOrder.id,
                                mode: CreateOrderDialogMode.EDIT,
                                activeOrderTab: CreateOrderType.MEETING_SERVICE,
                            }),
                        onDuplicate: () =>
                            openDialog({
                                orderId: drawerOrder.id,
                                mode: CreateOrderDialogMode.DUPLICATE,
                                activeOrderTab: CreateOrderType.MEETING_SERVICE,
                            }),
                        onDelete: () =>
                            setConfirmDialogState({
                                showConfirmDialog: true,
                                items: [drawerOrder],
                                type: DialogType.DELETE,
                            }),
                    }}
                />
            )}
        </Stack>
    )
}
